// The pure decisions of a tab's device check (#24 phase 3): what public-ledger/update-device-login-timestamp answered,
// which check is still the current one for a profile, and when another tab's change means watching the device again.
// The service (ns-services/ns-public-ledger-service.ts) makes the calls and keeps the state.

import { walletStorageKey } from './wallet-key-store.js';

/**
 * What public-ledger/update-device-login-timestamp answered: the device is signed out of the profile's account
 * (clearLocalStorage, also the answer to an enrolment whose replaced row is missing, someone else's or signed out), or it
 * is signed in, with the id of the row it recorded or enrolled (null when it named none).
 */
export type DeviceCheckAnswer = { signedOut: true } | { signedOut: false; deviceId: string | null };

export function deviceCheckAnswer(res: unknown): DeviceCheckAnswer {
  const answer = (res ?? {}) as { clearLocalStorage?: unknown; deviceId?: unknown };
  if (answer.clearLocalStorage === true) return { signedOut: true };
  const deviceId = typeof answer.deviceId === 'string' && answer.deviceId ? answer.deviceId : null;
  return { signedOut: false, deviceId };
}

/**
 * Which device check is the current one for each profile. Starting a check, forgetting the profile's wallet and a new
 * wallet (added here or in another tab) each move the profile on, so an older check still out stops before it acts on its
 * answer (stores an id, enrols, watches, forgets).
 */
export class DeviceCheckGenerations {
  private readonly __current = new Map<string, number>();

  /** The profile moves on; returns its new generation, for a check that starts now. */
  next(profileId: string): number {
    const generation = (this.__current.get(profileId) ?? 0) + 1;
    this.__current.set(profileId, generation);
    return generation;
  }

  /** Whether a check started at `generation` is still the profile's current one. */
  isCurrent(profileId: string, generation: number): boolean {
    return this.__current.get(profileId) === generation;
  }
}

/**
 * Whether another tab's localStorage change means this tab watches the profile's device again: that tab stored the
 * profile's key (a recovery or a new account) or its device id (a check that recorded or enrolled one).
 */
export function storageEventRewatches(
  event: { key: string | null; newValue: string | null },
  profileId: string | null | undefined,
): boolean {
  if (!profileId || !event.key || !event.newValue) return false;
  return (
    event.key === walletStorageKey(profileId, 'privateKey') || event.key === walletStorageKey(profileId, 'deviceId')
  );
}

/**
 * The watched row was signed out, but this browser now holds another device id for the profile: a tab enrolled a new row
 * in its place (which signs the old one out), so the new row is watched rather than the wallet forgotten.
 */
export function watchedDeviceReplaced(storedId: string | null | undefined, watchedId: string): boolean {
  return !!storedId && storedId !== watchedId;
}

/**
 * Whether another tab has already enrolled a new device row in place of `replacesDeviceId`: the id this browser now stores
 * for the profile, when it is set and isn't the replaced one. That row carries the same device key (the key pair is one per
 * profile in this browser), so the tab takes it instead of enrolling again; null when the enrolment is still to be made.
 */
export function enrolledByAnotherTab(storedNow: string | null, replacesDeviceId: string): string | null {
  return storedNow && storedNow !== replacesDeviceId ? storedNow : null;
}

/**
 * The row a signed-in answer moves this browser to, in place of `storedId`: the id the server answered when it is set and
 * isn't the stored one. For a device with no id stored, that is the row the server recorded; for one asking about a row
 * replaced since (a re-enrolment, here or in another tab), the server follows the replaced row's replacedBy link to the
 * signed-in row that took its place (same profile, carrying the device key sent). Null when the stored row stays the one
 * to watch.
 */
export function followedDeviceId(storedId: string | null, answeredId: string | null): string | null {
  return answeredId && answeredId !== storedId ? answeredId : null;
}

/**
 * Whether a device check may store a new device id for the profile: only while the id it asked about (`expected`, null for
 * none) is still the stored one and the profile's key is still held, so it never writes over another tab's change or
 * brings a device id back after the wallet was forgotten.
 */
export function mayStoreDeviceId(storedNow: string | null, expected: string | null, keyHeld: boolean): boolean {
  return keyHeld && (storedNow || null) === (expected || null);
}

/**
 * How a watch on this device's plDevice row was started, which decides what a row it reads as signed out leads to:
 * - `checked`: right after a device check the server answered (signed in), so that answer was fresh;
 * - `failed`: after a device check that failed (offline, a server error, or a re-enrolment whose answer was lost after the
 *   server had made the new row and signed the old one out), so the server's word on the row is still to be had;
 * - `retried`: after a check that was itself the one fresh check a failed check's watch asked for, and failed too;
 * - `plain`: any other watch (another tab stored the profile's key or device id).
 */
export type DeviceWatchStart = 'checked' | 'failed' | 'retried' | 'plain';

/**
 * What a signed-out row seen by the watch leads to: the wallet is forgotten (`forget`), a fresh device check asks the
 * server first (`recheck`, or `recheckOnce` for the one fresh check a failed check's watch asks for), or nothing yet
 * (`wait`: the wallet is kept, and the failed check is tried again when the tab is back online or focused, the realtime
 * connection resyncs, or before the next signature).
 */
export type SignedOutRowAction = 'forget' | 'recheck' | 'recheckOnce' | 'wait';

/**
 * What the watch does with a signed-out row, read when the watch starts (`firstRead`) or from a later realtime change.
 * Only a watch started right after a check the server answered forgets straight away. A failed check proves nothing: the
 * row may be one this device's own re-enrolment signed out (its new id lost with the answer), and a fresh check is answered
 * with the new row (the server follows the replaced row's replacedBy link) or confirms the sign-out. That fresh check is asked once
 * per failure, so a server out of reach isn't asked again and again; a later realtime change asks again.
 */
export function signedOutRowAction(start: DeviceWatchStart, firstRead: boolean): SignedOutRowAction {
  if (!firstRead) return 'recheck';
  if (start === 'checked') return 'forget';
  if (start === 'failed') return 'recheckOnce';
  if (start === 'retried') return 'wait';
  return 'recheck';
}

// How long a tab waits for the profile's device lock (another tab may have stalled holding it), and how long each server
// call made under it may take: either running out fails the device check, which keeps the wallet and is tried again when
// the tab is back online or focused, the realtime connection resyncs, or before the next signature
export const DEVICE_LOCK_WAIT_MS = 30_000;
export const DEVICE_CALL_TIMEOUT_MS = 15_000;

/**
 * How a device check ended: the server answered the device signed in (on `deviceId`, the row now watched; null when none
 * was recorded) or signed out (the wallet was forgotten), the check failed (offline, a server error, a timeout), or it went
 * stale before acting on an answer (the wallet was forgotten or replaced, the profile changed, or a newer check started).
 */
export type DeviceCheckOutcome =
  | { kind: 'signedIn'; deviceId: string | null }
  | { kind: 'signedOut' }
  | { kind: 'failed' }
  | { kind: 'stale' };

/**
 * What a device check's outcome means for a profile this tab holds as unconfirmed (a signature refused as signed out whose
 * fresh check couldn't answer): `confirmed` when the server answered the device signed in (on any row), `signedOut` when it
 * answered signed out (the wallet was forgotten), and `unconfirmed` while it still hasn't answered (the check failed or
 * went stale), so signatures and device proofs stay refused up front.
 */
export function deviceConfirmation(outcome: DeviceCheckOutcome): 'confirmed' | 'signedOut' | 'unconfirmed' {
  if (outcome.kind === 'signedIn') return 'confirmed';
  if (outcome.kind === 'signedOut') return 'signedOut';
  return 'unconfirmed';
}

/**
 * What a signature the device gate refused as signed out (DEVICE_SIGNED_OUT) leads to, once the fresh device check it ran
 * has ended. The wallet is forgotten (`forget`) only when that check answers the device signed out. It is kept (`keep`)
 * when the check answers it signed in, on the refused row (`refusedId`) or another: the row the server followed from the
 * refused one's replacedBy link, or one another tab enrolled and stored meanwhile. A check that went stale while this
 * browser now stores another row for the profile (`storedNow`, another tab moved the device on) is asked once more
 * (`recheck`, only on the `first` check). Otherwise the check couldn't answer (it failed, timed out or went stale): the
 * wallet is kept but the profile is held unconfirmed in this tab (`unconfirm`), refusing every signature and device proof
 * up front until a device check answers.
 */
export function refusedSignatureAction(
  refusedId: string | null,
  outcome: DeviceCheckOutcome,
  storedNow: string | null,
  first: boolean,
): 'keep' | 'recheck' | 'unconfirm' | 'forget' {
  if (outcome.kind === 'signedIn') return 'keep';
  if (outcome.kind === 'signedOut') return 'forget';
  if (outcome.kind === 'stale' && first && storedNow && storedNow !== refusedId) return 'recheck';
  return 'unconfirm';
}
